import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Users, DollarSign, TrendingUp, Copy, Share2, QrCode, 
  Wallet, ArrowDownToLine, User, LogOut, Globe, BookOpen,
  CheckCircle, Clock, ChevronRight, Sparkles, Gift, Target
} from 'lucide-react';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [qrData, setQrData] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, refRes, commRes, qrRes] = await Promise.all([
        api().get('/partner/dashboard'),
        api().get('/partner/referrals'),
        api().get('/partner/commissions'),
        api().get('/partner/qr-data')
      ]);
      setDashboard(dashRes.data);
      setReferrals(refRes.data);
      setCommissions(commRes.data);
      setQrData(qrRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(language === 'en' ? 'Error loading data' : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [api, language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(dashboard?.codigo_compa || '');
      toast.success(language === 'en' ? 'Code copied!' : '¡Código copiado!');
    } catch (err) {
      // Fallback for when clipboard API fails
      toast.info(language === 'en' ? `Code: ${dashboard?.codigo_compa}` : `Código: ${dashboard?.codigo_compa}`);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(qrData?.referral_link || '');
      toast.success(language === 'en' ? 'Link copied!' : '¡Link copiado!');
    } catch (err) {
      // Fallback - show the link in a toast
      toast.info(qrData?.referral_link || '', { duration: 5000 });
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'BidTrip - Experiencias Increíbles',
          text: language === 'en' 
            ? `Join BidTrip with my code ${dashboard?.codigo_compa} and book amazing experiences!`
            : `¡Únete a BidTrip con mi código ${dashboard?.codigo_compa} y reserva experiencias increíbles!`,
          url: qrData?.referral_link
        });
      } catch (err) {
        // User cancelled share dialog, that's okay - don't do anything
        if (err.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  return (
    <MobileShell>
      <TopBar
        showBrand
        right={<span className="ml-1 text-[10px] bg-gold text-foreground px-2 py-0.5 rounded-full font-bold">PARTNER</span>}
      />
      <main className="px-4 py-4" data-testid="partner-dashboard">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">
            {language === 'en' ? `Hello, ${user?.name?.split(' ')[0]}!` : `¡Hola, ${user?.name?.split(' ')[0]}!`}
          </h1>
          <p className="text-muted-foreground">
            {language === 'en' ? 'Welcome to your Partner Dashboard' : 'Bienvenido a tu Panel de Partner'}
          </p>
        </div>

        {/* Código Compa Card */}
        <Card className="mb-6 border-0 shadow-xl bg-gradient-to-br from-gold to-gold/80 text-foreground overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm opacity-80 mb-1">
                  {language === 'en' ? 'Your Código Compa' : 'Tu Código Compa'}
                </p>
                <div className="flex items-center gap-3">
                  <span className="font-heading text-4xl font-bold tracking-wider">
                    {dashboard?.codigo_compa}
                  </span>
                  <button
                    onClick={copyCode}
                    className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  >
                    <Copy size={20} />
                  </button>
                </div>
                <p className="text-sm mt-2 opacity-80">
                  {language === 'en' 
                    ? 'Share this code with tourists to earn 5% on their bookings!'
                    : '¡Comparte este código con turistas y gana 5% de sus reservas!'}
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowQrModal(true)}
                  className="bg-card text-foreground hover:bg-white/90"
                >
                  <QrCode size={18} className="mr-2" />
                  {language === 'en' ? 'Show QR' : 'Ver QR'}
                </Button>
                <Button
                  onClick={shareLink}
                  className="bg-foreground text-white hover:bg-foreground/90"
                >
                  <Share2 size={18} className="mr-2" />
                  {language === 'en' ? 'Share' : 'Compartir'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-caribbean/10 rounded-xl">
                  <DollarSign className="text-caribbean" size={24} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'en' ? 'Today' : 'Hoy'}
                  </p>
                  <p className="font-heading text-xl font-bold">${dashboard?.earnings?.today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rosa/10 rounded-xl">
                  <TrendingUp className="text-rosa" size={24} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'en' ? 'This Week' : 'Esta Semana'}
                  </p>
                  <p className="font-heading text-xl font-bold">${dashboard?.earnings?.week || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gold/10 rounded-xl">
                  <Target className="text-gold" size={24} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'en' ? 'This Month' : 'Este Mes'}
                  </p>
                  <p className="font-heading text-xl font-bold">${dashboard?.earnings?.month || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-foreground/10 rounded-xl">
                  <Users className="text-foreground" size={24} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'en' ? 'Referrals' : 'Referidos'}
                  </p>
                  <p className="font-heading text-xl font-bold">{dashboard?.stats?.total_referrals || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Balance & Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Balance Card */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-caribbean to-caribbean/80 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Wallet size={24} />
                <span className="text-white/80">
                  {language === 'en' ? 'Available Balance' : 'Saldo Disponible'}
                </span>
              </div>
              <p className="font-heading text-4xl font-bold mb-2">
                ${dashboard?.balance?.available || 0} <span className="text-lg font-normal">USD</span>
              </p>
              <div className="flex gap-4 mt-4 pt-4 border-t border-white/20 text-sm">
                <div>
                  <p className="text-white/70">{language === 'en' ? 'Total Earned' : 'Total Ganado'}</p>
                  <p className="font-bold">${dashboard?.earnings?.total || 0}</p>
                </div>
                <div>
                  <p className="text-white/70">{language === 'en' ? 'Withdrawn' : 'Retirado'}</p>
                  <p className="font-bold">${dashboard?.balance?.withdrawn || 0}</p>
                </div>
              </div>
              {dashboard?.balance?.pending > 0 && (
                <div className="mt-4 p-3 bg-gold/20 rounded-xl">
                  <p className="text-sm text-gold">
                    <Clock size={14} className="inline mr-1" />
                    {language === 'en' 
                      ? `$${dashboard.balance.pending} pending release`
                      : `$${dashboard.balance.pending} pendiente de liberación`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stripe Connect card — auto payouts */}
          <StripeConnectCard api={api} language={language} />

          {/* Quick Actions */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles size={20} />
                {language === 'en' ? 'Quick Actions' : 'Acciones Rápidas'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => setShowGuideModal(true)}
                variant="outline"
                className="w-full justify-between h-14 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <BookOpen size={20} className="text-caribbean" />
                  <span>{language === 'en' ? 'How to Use BidTrip' : 'Cómo Usar BidTrip'}</span>
                </div>
                <ChevronRight size={18} />
              </Button>
              <Button
                onClick={copyLink}
                variant="outline"
                className="w-full justify-between h-14 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <Copy size={20} className="text-rosa" />
                  <span>{language === 'en' ? 'Copy Referral Link' : 'Copiar Link de Referido'}</span>
                </div>
                <ChevronRight size={18} />
              </Button>
              <Link to="/profile">
                <Button variant="outline" className="w-full justify-between h-14 rounded-xl">
                  <div className="flex items-center gap-3">
                    <User size={20} className="text-gold" />
                    <span>{language === 'en' ? 'Edit Profile' : 'Editar Perfil'}</span>
                  </div>
                  <ChevronRight size={18} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Referrals and Commissions */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview">
              {language === 'en' ? 'My Referrals' : 'Mis Referidos'}
            </TabsTrigger>
            <TabsTrigger value="commissions">
              {language === 'en' ? 'Commissions' : 'Comisiones'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift size={20} />
                  {language === 'en' ? 'Referred Tourists' : 'Turistas Referidos'}
                </CardTitle>
                <CardDescription>
                  {language === 'en' 
                    ? 'Tourists who registered with your Código Compa'
                    : 'Turistas que se registraron con tu Código Compa'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users size={48} className="mx-auto mb-4 opacity-50" />
                    <p>{language === 'en' ? 'No referrals yet' : 'Aún no tienes referidos'}</p>
                    <p className="text-sm mt-2">
                      {language === 'en' 
                        ? 'Share your Código Compa to start earning!'
                        : '¡Comparte tu Código Compa para empezar a ganar!'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((ref) => (
                      <div key={ref.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-caribbean/10 rounded-full flex items-center justify-center">
                            <User className="text-caribbean" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold">{ref.tourist_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(ref.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-caribbean">${ref.total_commission.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">
                            {ref.total_purchases} {language === 'en' ? 'purchases' : 'compras'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="commissions">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign size={20} />
                  {language === 'en' ? 'Commission History' : 'Historial de Comisiones'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {commissions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
                    <p>{language === 'en' ? 'No commissions yet' : 'Sin comisiones aún'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {commissions.map((comm) => (
                      <div key={comm.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                        <div>
                          <p className="font-semibold">{comm.booking_category}</p>
                          <p className="text-sm text-muted-foreground">{comm.booking_location}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(comm.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-heading font-bold text-lg text-caribbean">
                            +${comm.commission_amount.toFixed(2)}
                          </p>
                          <Badge className={comm.status === 'available' ? 'bg-green-500' : 'bg-gold'}>
                            {comm.status === 'available' 
                              ? (language === 'en' ? 'Available' : 'Disponible')
                              : (language === 'en' ? 'Pending' : 'Pendiente')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {language === 'en' ? 'Your QR Code' : 'Tu Código QR'}
            </DialogTitle>
            <DialogDescription className="text-center">
              {language === 'en' 
                ? 'Tourists scan this to register with your code'
                : 'Los turistas escanean esto para registrarse con tu código'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center p-4">
            <div className="p-4 bg-card rounded-2xl shadow-lg">
              <QRCodeSVG 
                value={qrData?.qr_value || ''} 
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="mt-4 font-heading text-2xl font-bold">{dashboard?.codigo_compa}</p>
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {language === 'en' 
                ? 'Tourist registers → Makes a booking → You earn 5%!'
                : 'Turista se registra → Hace una reserva → ¡Tú ganas 5%!'}
            </p>
            <div className="flex gap-3 mt-6">
              <Button onClick={copyLink} variant="outline" className="rounded-full">
                <Copy size={16} className="mr-2" />
                {language === 'en' ? 'Copy Link' : 'Copiar Link'}
              </Button>
              <Button onClick={shareLink} className="rounded-full bg-caribbean">
                <Share2 size={16} className="mr-2" />
                {language === 'en' ? 'Share' : 'Compartir'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guide Modal */}
      <Dialog open={showGuideModal} onOpenChange={setShowGuideModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'en' ? 'Partner Guide' : 'Guía del Partner'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-caribbean rounded-full flex items-center justify-center text-white font-bold">1</div>
              <div>
                <h4 className="font-semibold">{language === 'en' ? 'Share Your Code' : 'Comparte Tu Código'}</h4>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' 
                    ? 'Share your Código Compa with tourists. You can use the QR code, link, or just tell them the code.'
                    : 'Comparte tu Código Compa con turistas. Puedes usar el código QR, el link, o simplemente decirles el código.'}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-rosa rounded-full flex items-center justify-center text-white font-bold">2</div>
              <div>
                <h4 className="font-semibold">{language === 'en' ? 'Tourist Registers' : 'El Turista se Registra'}</h4>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' 
                    ? 'When a tourist registers using your code, they become your referral forever. All their future purchases count!'
                    : 'Cuando un turista se registra usando tu código, se convierte en tu referido para siempre. ¡Todas sus compras futuras cuentan!'}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-gold rounded-full flex items-center justify-center text-foreground font-bold">3</div>
              <div>
                <h4 className="font-semibold">{language === 'en' ? 'Tourist Books Experience' : 'El Turista Reserva Experiencia'}</h4>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' 
                    ? 'The tourist creates a request, receives offers from providers, and books an experience. You don\'t have to do anything!'
                    : 'El turista crea una solicitud, recibe ofertas de proveedores, y reserva una experiencia. ¡Tú no tienes que hacer nada!'}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-foreground rounded-full flex items-center justify-center text-white font-bold">4</div>
              <div>
                <h4 className="font-semibold">{language === 'en' ? 'You Earn 5%' : 'Tú Ganas 5%'}</h4>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' 
                    ? "When the tourist pays the reservation, you automatically earn 5% of the total. No action needed!"
                    : 'Cuando el turista paga la reserva, automáticamente ganas el 5% del total. ¡Sin mover un dedo!'}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-[#635BFF] rounded-full flex items-center justify-center text-white font-bold">5</div>
              <div>
                <h4 className="font-semibold flex items-center gap-2">
                  {language === 'en' ? 'Connect Your Stripe' : 'Conecta tu Stripe'}
                  <Badge className="bg-[#635BFF] text-white text-[10px]">NEW</Badge>
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {language === 'en'
                    ? "One-time setup (~5 min). Tap \"Connect with Stripe\" up top → fill your name, RFC and bank CLABE on Stripe. From then on, every commission lands automatically. Stripe deposits to your bank in ~2 business days."
                    : 'Configuración única (~5 min). Toca "Conectar con Stripe" arriba → llena tu nombre, RFC y CLABE en Stripe. Desde ahí, cada comisión te cae automático. Stripe deposita a tu banco en ~2 días hábiles.'}
                </p>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1 ml-1">
                  <li>• {language === 'en' ? 'You set up: name, RFC, CURP, address, bank CLABE, ID upload.' : 'Llenas: nombre, RFC, CURP, domicilio, CLABE, foto de tu INE.'}</li>
                  <li>• {language === 'en' ? 'Without Stripe connected we hold your commission as pending.' : 'Sin Stripe conectado, tus comisiones quedan en pendiente.'}</li>
                  <li>• {language === 'en' ? 'Open your Stripe anytime from the Dashboard.' : 'Abre tu Stripe cuando quieras desde el Dashboard.'}</li>
                </ul>
              </div>
            </div>

            <div className="p-4 bg-caribbean/10 rounded-xl">
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <Sparkles className="text-caribbean" size={18} />
                {language === 'en' ? 'Pro Tips' : 'Tips Pro'}
              </h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• {language === 'en' ? 'Put your QR code in visible places' : 'Pon tu código QR en lugares visibles'}</li>
                <li>• {language === 'en' ? 'Share on your social media' : 'Comparte en tus redes sociales'}</li>
                <li>• {language === 'en' ? 'Tell tourists about great experiences in BidTrip' : 'Cuéntale a los turistas sobre las experiencias increíbles en BidTrip'}</li>
                <li>• {language === 'en' ? 'The more referrals, the more you earn!' : '¡Entre más referidos, más ganas!'}</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MobileShell>
  );
}

function StripeConnectCard({ api, language }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api().get('/partner/stripe/status');
      setStatus(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto refresh on return from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'done') {
      fetchStatus();
      toast.success(language === 'en' ? 'Stripe linked ✅' : '¡Stripe conectado! ✅');
      const url = window.location.pathname;
      window.history.replaceState({}, '', url);
    }
  }, [fetchStatus, language]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api().post('/partner/stripe/connect');
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Stripe error');
    } finally {
      setConnecting(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const { data } = await api().get('/partner/stripe/login-link');
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Stripe error');
    }
  };

  if (loading) return null;

  const isReady = status?.connected && status?.payouts_enabled;
  const needsMore = status?.connected && !status?.payouts_enabled;

  return (
    <Card data-testid="partner-stripe-card" className="border-0 shadow-lg overflow-hidden">
      <div className="p-5 bg-gradient-to-br from-[#635BFF] to-[#3F32D6] text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs opacity-80 uppercase tracking-wider font-semibold">
              {language === 'en' ? 'Auto payouts' : 'Pagos automáticos'}
            </p>
            <p className="font-heading text-lg font-bold mt-1">
              {isReady
                ? (language === 'en' ? 'Stripe linked ✅' : 'Stripe conectado ✅')
                : (language === 'en' ? 'Get paid automatically' : 'Cobra automático')}
            </p>
          </div>
          <Wallet size={28} className="opacity-90 shrink-0" />
        </div>
        <p className="text-xs opacity-90 mt-2 leading-snug">
          {isReady
            ? (language === 'en'
                ? "Every commission lands in your Stripe Express account. Stripe pays out to your bank automatically."
                : 'Cada comisión cae en tu Stripe Express. Stripe te deposita a tu banco automáticamente.')
            : (language === 'en'
                ? "Link Stripe once and every 5% commission you earn will be sent automatically."
                : 'Conecta Stripe una vez y cada 5% que ganes se manda automático.')}
        </p>

        {!status?.connected && (
          <Button
            onClick={connect}
            disabled={connecting}
            data-testid="partner-connect-stripe-btn"
            className="mt-4 w-full bg-white text-[#635BFF] hover:bg-white/90 font-semibold rounded-full"
          >
            {connecting
              ? (language === 'en' ? 'Loading…' : 'Cargando…')
              : (language === 'en' ? 'Connect with Stripe' : 'Conectar con Stripe')}
          </Button>
        )}

        {needsMore && (
          <Button
            onClick={connect}
            disabled={connecting}
            data-testid="partner-finish-stripe-btn"
            className="mt-4 w-full bg-white text-[#635BFF] hover:bg-white/90 font-semibold rounded-full"
          >
            {connecting
              ? (language === 'en' ? 'Loading…' : 'Cargando…')
              : (language === 'en' ? 'Finish onboarding' : 'Termina tu registro')}
          </Button>
        )}

        {isReady && (
          <Button
            onClick={openStripeDashboard}
            data-testid="partner-stripe-dashboard-btn"
            variant="outline"
            className="mt-4 w-full bg-white/15 text-white border-white/40 hover:bg-white/25 font-semibold rounded-full"
          >
            {language === 'en' ? 'Open my Stripe dashboard' : 'Abrir mi Stripe'}
          </Button>
        )}
      </div>
    </Card>
  );
}
